import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, FileText, Download, Loader2, CheckCircle, BookOpen } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { formatDate, formatDateTime } from '../lib/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_NAMES = {
    diferencial_inicial: 'Diferencial inicial', registro_episodios: 'Registro episodios',
    semaforo: 'Semáforo conductual', doble_capa: 'Doble capa', linea_base: 'Línea base',
    mapa_amenaza: 'Mapa amenaza', radar_exigencia: 'Radar exigencia',
    contrato_oculto: 'Contrato oculto', arbol_temor: 'Árbol temor', radiografia: 'Radiografía',
    inventario_frentes: 'Inventario frentes', deber_prestado: 'Deber prestado',
    filtro_impacto: 'Filtro impacto', tarea_faro_3: 'Tarea faro', dai: 'DAI',
    descomposicion_momento: 'Descomposición momento', microsenales: 'Microseñales',
    reescritura: 'Reescritura', intervencion_vivo: 'Intervención en vivo', automatizacion: 'Automatización',
    detector_friccion: 'Detector de fricción', vmv: 'Versión mínima viable',
    diseno_entorno: 'Diseño de entorno', arranque_obligatorio: 'Arranque obligatorio',
    sistema_ejecucion: 'Sistema de ejecución',
    registro_accion: 'Registro de acción visible', cadena_ejecucion: 'Cadena de ejecución',
    escala_ajuste: 'Escala de ajuste', metricas_funcionales: 'Métricas funcionales',
    ciclo_progreso: 'Ciclo de progreso',
    evaluacion_funcional: 'Evaluación funcional', mapa_patrones: 'Mapa de patrones',
    punto_intervencion: 'Punto de intervención', rediseno_patron: 'Rediseño de patrón',
    mapa_decision: 'Mapa de decisión',
    diagnostico_sostenibilidad: 'Revisión de sostenibilidad', reinicio_rapido: 'Reinicio rápido',
    proteccion_recaidas: 'Protección contra recaídas', refuerzo_inteligente: 'Refuerzo inteligente',
    sistema_autonomo: 'Sistema autónomo',
    senales_integracion: 'Señales de integración', transferencia_sistema: 'Transferencia del sistema',
    criterio_propio: 'Criterio propio', identidad_operativa: 'Identidad operativa',
    vision_operativa: 'Visión operativa',
};

const CHAPTER_NAMES = { 1: 'Autoexploración', 2: 'Amenaza y Defensa', 3: 'Foco y Acción', 4: 'Intervención y Automatización', 5: 'Ejecución sin Fricción', 6: 'Sostenimiento y Progreso', 7: 'Evaluación y Rediseño', 8: 'Revisión y Sistema Autónomo', 9: 'Integración y Cierre' };

export default function MisInformes() {
    const [reports, setReports] = useState([]);
    const [observations, setObservations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            try {
                const [reps, obs] = await Promise.all([
                    axios.get(`${API_URL}/api/user/reports`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/user/observations`, { withCredentials: true })
                ]);
                setReports(reps.data);
                setObservations(obs.data);
            } catch { toast.error('Error al cargar datos'); }
            finally { setLoading(false); }
        };
        fetch();
    }, []);

    if (loading) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']" data-testid="mis-informes-title">Mis Informes</h1>
                                <p className="text-xs text-[#7A8A93]">Informes de progreso y observaciones</p>
                            </div>
                        </div>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/manual/download`} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm" className="border-[#5FAF9D]/30 text-[#5FAF9D] hover:bg-[#5FAF9D]/5" data-testid="manual-link"><BookOpen className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Manual</span></Button>
                        </a>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1">
                {/* Approved Reports */}
                <h2 className="text-base font-semibold text-[#1F2A30] mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-[#3E5F6E]" />Informes de progreso</h2>
                {reports.length === 0 ? (
                    <Card className="bg-white border-[#DCE3E8] mb-8"><CardContent className="py-8 text-center"><FileText className="h-10 w-10 mx-auto text-[#DCE3E8] mb-2" /><p className="text-sm text-[#7A8A93]">Aún no tienes informes disponibles</p></CardContent></Card>
                ) : (
                    <div className="space-y-3 mb-8">
                        {reports.map(report => {
                            const content = report.edited_content || report.ai_content || {};
                            return (
                                <Card key={report.id} className="bg-white border-[#DCE3E8] shadow-sm" data-testid={`user-report-${report.id}`}>
                                    <CardContent className="py-4">
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-[#1F2A30]">{MODULE_NAMES[report.module_id] || report.module_id}</p>
                                                    <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-[10px]"><CheckCircle className="h-3 w-3 mr-0.5" />Aprobado</Badge>
                                                </div>
                                                <p className="text-xs text-[#7A8A93]">Cap. {report.chapter}: {CHAPTER_NAMES[report.chapter]} - {report.approved_at ? formatDate(report.approved_at) : ''}</p>
                                            </div>
                                        </div>
                                        <div className="p-3 bg-[#F5F7F8] rounded-xl text-sm text-[#4A5B63]">
                                            {content.resumen || content.insight_usuario || 'Informe de progreso aprobado por tu mentora.'}
                                        </div>
                                        {content.recomendacion && (
                                            <div className="mt-2 p-3 bg-[#5FAF9D]/5 border-l-3 border-[#5FAF9D] rounded-r-xl text-sm text-[#4A5B63]">
                                                <p className="text-xs font-semibold text-[#5FAF9D] mb-1">Recomendación</p>
                                                {content.recomendacion}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Observations */}
                {observations.length > 0 && (
                    <>
                        <h2 className="text-base font-semibold text-[#1F2A30] mb-3 flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#5FAF9D]" />Observaciones de tu mentora</h2>
                        <div className="space-y-3">
                            {observations.map((obs) => (
                                <Card key={obs.id || obs.created_at} className="bg-white border-[#DCE3E8] shadow-sm" data-testid={`user-observation-${obs.id || obs.module_id}`}>
                                    <CardContent className="py-3">
                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                            {obs.module_id && <Badge className="bg-[#3E5F6E]/10 text-[#3E5F6E] text-[10px]">{MODULE_NAMES[obs.module_id] || obs.module_id}</Badge>}
                                            {obs.chapter && <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-[10px]">Cap. {obs.chapter}</Badge>}
                                            <span className="text-[10px] text-[#7A8A93]">{obs.created_at ? formatDate(obs.created_at) : ''}</span>
                                        </div>
                                        <p className="text-sm text-[#4A5B63]">{obs.text}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </>
                )}
            </main>
            <SENDAFooter />
        </div>
    );
}
